import { useState, useEffect } from 'react';
import useUserMetaState from 'modules/queries/account/user/stateData/useUserMetaState';

const UPSELL_KEY_PREFIX = 'upsell:';
const ASSET_UPSELL_KEY_PREFIX = `${UPSELL_KEY_PREFIX}asset:`;

const DEFAULT_GRACE_PERIOD_MS = 10 * 1000; // 10 seconds

export const useInGracePeriod = (
	dismissTime?: number,
	gracePeriodMs: number = DEFAULT_GRACE_PERIOD_MS,
) => {
	const [inGracePeriod, setInGracePeriod] = useState(false);
	const [timeRemaining, setTimeRemaining] = useState(0);

	useEffect(() => {
		if (!dismissTime) {
			setInGracePeriod(false);
			setTimeRemaining(0);
			return undefined;
		}

		const checkExpiration = () => {
			const now = Date.now();
			const timeSinceUpdate = now - (dismissTime || 0);
			const isExpired = timeSinceUpdate > gracePeriodMs;

			if (isExpired) {
				setInGracePeriod(false);
				setTimeRemaining(0);
				return true;
			}

			const remaining = gracePeriodMs - timeSinceUpdate;
			setInGracePeriod(true);
			setTimeRemaining(remaining);
			return false;
		};

		const shouldStop = checkExpiration();
		if (shouldStop) {
			return undefined;
		}

		const interval = setInterval(() => {
			const shouldStopInterval = checkExpiration();
			if (shouldStopInterval) {
				clearInterval(interval);
			}
		}, 50);

		return () => clearInterval(interval);
	}, [dismissTime, gracePeriodMs]);

	return {
		inGracePeriod,
		timeRemaining,
		percentTimeRemaining: (timeRemaining / gracePeriodMs) * 100,
	};
};

export const useAssetBackupsUpsell = (assetUniqId: string) =>
	useUserMetaState(
		`${ASSET_UPSELL_KEY_PREFIX}backups:${assetUniqId}:dismissed`,
		false,
		{
			safe: false,
		},
	);

export const useAssetDdosProtectionUpsell = (assetUniqId: string) =>
	useUserMetaState(
		`${ASSET_UPSELL_KEY_PREFIX}ddosProtection:${assetUniqId}:dismissed`,
		false,
		{
			safe: false,
		},
	);

export const useAssetImunify360Upsell = (assetUniqId: string) =>
	useUserMetaState(
		`${ASSET_UPSELL_KEY_PREFIX}imunify360:${assetUniqId}:dismissed`,
		false,
		{
			safe: false,
		},
	);

export const useAssetThreatdownUpsell = (assetUniqId: string) =>
	useUserMetaState(
		`${ASSET_UPSELL_KEY_PREFIX}threatdown:${assetUniqId}:dismissed`,
		false,
		{
			safe: false,
		},
	);
